<template>
    <div class="bg-image-component" :class="{'overflow-hidden': !overflow}">
        <div ref="bg-image-wrapper" class="bg-image-wrapper" :class="{
            'loaded': loaded,
            'blur': placeholder,
        }" :style="{backgroundColor: bgColor}">
            <div v-if="placeholder" ref="bg-placeholder" class="bg-image bg-image-low" :style="{
                    backgroundImage: `url(${placeholder})`,
                    opacity: opacity
                }">
            </div>

            <img v-if="lazyLoad" ref="image" :data-src="src" @load="imageLoadHandler()" style="display:none;">
            <img v-else ref="image" :src="src" @load="imageLoadHandler()" style="display:none;">

            <div ref="bg-image" class="bg-image bg-image-high" :style="{
                    opacity: opacity
                }">
            </div>

            <div v-if="overlay" class="overlay" :style="{'backgroundColor': overlay}"></div>
        </div>

        <slot></slot>
    </div>
</template>

<script>
    import 'intersection-observer'

    export default {
        props: {
            src: {
                type: String,
                required: true,
                default: null
            },
            placeholder: {
                type: String,
                required: false
            },
            bgColor: {
                type: String,
                required: false,
                default: null
            },
            effect: {
                type: Object,
                required: false,
                default: null
            },
            overflow: {
                type: Boolean,
                required: false,
                default: true
            },
            debug: {
                type: Boolean,
                required: false,
                default: false
            },
            opacity: {
                type: Number,
                required: false,
                default: null
            },
            overlay: {
                type: String,
                required: false,
                default: null
            },
            lazyLoad: {
                type: Boolean,
                required: false,
                default: true
            }
        },
        data() {
            return {
                observer: null,
                zoomObserver: null,
                zoomObserverTriggered: false,
                loaded: false,
            }
        },
        methods: {
            imageLoadHandler() {
                this.$emit('loaded')

                if (this.lazyLoad) {
                    this.$refs['bg-image'].style.backgroundImage = `url(${this.$refs['image'].getAttribute('data-src')})`
                } else {
                    this.$refs['bg-image'].style.backgroundImage = `url(${this.$refs['image'].getAttribute('src')})`
                }

                if (this.effect && this.zoomObserverTriggered || this.effect && this.effect.isHero) {
                    setTimeout(() => {
                        this.$refs['bg-image-wrapper'].classList.add(this.effect.type)
                        // this.$el.classList.add(this.effect.type)
                    }, 0);
                }

                if (!this.debug) {
                    this.loaded = true;
                }
            },
            loadHighResImage(entries) {
                entries.forEach(({
                    target,
                    isIntersecting
                }) => {

                    if (!isIntersecting) {
                        return;
                    }

                    this.$refs['image'].setAttribute('src', this.$refs['image'].getAttribute('data-src'))

                    this.observer.unobserve(target);
                });
            },
            animateZoom(entries) {
                entries.forEach(({
                    target,
                    isIntersecting
                }) => {
                    if (!isIntersecting) {
                        return;
                    }

                    if (this.loaded) {
                        target.classList.add(this.effect.type)
                    }

                    this.zoomObserverTriggered = true
                    this.zoomObserver.unobserve(target)
                });
            }
        },
        created() {
            if (this.lazyLoad) {
                this.observer = new IntersectionObserver(
                    this.loadHighResImage, {
                        root: this.$el,
                        threshold: 0,
                    }
                )
            }

            if (this.effect) {
                this.zoomObserver = new IntersectionObserver(
                    this.animateZoom, {
                        root: this.$el,
                        threshold: this.effect.threshold ? this.effect.threshold : 1,
                    }
                )
            }
        },
        mounted() {
            if (this.lazyLoad) {
                this.observer.observe(this.$el)
            } else {

            }

            if (this.effect) {
                this.zoomObserver.observe(this.$el)
            }
        },
        beforeDestroy() {
            if (this.lazyLoad) {
                this.observer.disconnect()
            }

            if (this.effect) {
                this.zoomObserver.disconnect()
            }
        }
    }
</script>

<style lang="scss">
    bg-image {
        display: block;
        position: relative;
    }

    .bg-image-component {
        position: relative;

        .bg-image-wrapper {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            overflow: hidden;

            // &.overflow-hidden {
            //     overflow: hidden;
            // }

            .overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 100%;
            }

            .bg-image {
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0%;
                left: 0%;
                background-position: 50% 50%;
                background-size: cover;
                background-repeat: no-repeat;
                backface-visibility: hidden;

                &.bg-image-low {
                    opacity: 1;
                    transition: opacity 500ms 500ms ease-out;
                }

                &.bg-image-high {
                    opacity: 0;
                    transition: opacity 500ms ease-out;
                }
            }

            &.blur {
                .bg-image {
                    &.bg-image-low {
                        filter: blur(20px);
                        transition: opacity 500ms 500ms ease-out, filter 1000ms ease-out;
                    }

                    &.bg-image-high {
                        filter: blur(20px);
                        transition: opacity 500ms ease-out, filter 1000ms ease-out;
                    }
                }
            }

            &.zoom-in {
                .bg-image {

                    &.bg-image-low,
                    &.bg-image-high {
                        transform: scale(1);
                        transition: opacity 500ms ease-out, filter 1000ms ease-out, transform 7000ms ease-out;
                    }
                }
            }

            &.loaded {
                .bg-image {
                    &.bg-image-low {
                        opacity: 0;
                    }

                    &.bg-image-high {
                        opacity: 1;
                    }
                }

                &.blur {
                    .bg-image {

                        &.bg-image-low,
                        &.bg-image-high {
                            filter: blur(0px);
                        }
                    }
                }

                &.zoom-in {
                    .bg-image {

                        &.bg-image-low,
                        &.bg-image-high {
                            transform: scale(1.05);
                        }
                    }
                }
            }
        }

        &.bg-contain {
            .bg-image-wrapper {
                .bg-image {
                    background-size: contain;
                }
            }
        }

        &.bg-left {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 0% 0%;
                }
            }
        }

        &.bg-right {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 100% 0%;
                }
            }
        }

        &.bg-top {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 50% 0%;
                }
            }
        }

        &.bg-bottom {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 50% 100%;
                }
            }
        }

        &.bg-bottom-left {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 0% 100%;
                }
            }
        }

        &.bg-x-20 {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 20% 0%;
                }
            }
        }

        &.bg-x-40 {
            .bg-image-wrapper {
                .bg-image {
                    background-position: 40% 0%;
                }
            }
        }

        &.bg-fixed {
            .bg-image-wrapper {
                position: fixed;
                left: 0;
                right: 0;
                z-index: -1;
            }
        }
    }

    // SM AND UP
    @media only screen and (min-width: 576px) {}

    // MD AND UP
    @media only screen and (min-width: 768px) {}

    // LG AND UP
    @media only screen and (min-width: 992px) {
        .bg-image-component {
            .bg-image-wrapper {
                &.bg-lg-left-30 {
                    .bg-image {
                        background-position: 30% 100%;
                    }
                }
            }

            .bg-image-wrapper {
                &.bg-lg-30-100 {
                    .bg-image {
                        background-position: 30% 100%;
                    }
                }
            }
        }
    }

    // XL AND UP
    @media only screen and (min-width: 1200px) {
        .bg-image-component {
            .bg-image-wrapper {
                &.bg-xl-left-10 {
                    .bg-image {
                        background-position: 0% 0%;
                    }
                }
            }

            .bg-image-wrapper {
                &.bg-xl-10-50 {
                    .bg-image {
                        background-position: 50% 10%;
                    }
                }
            }
        }
    }

    // XXL AND UP
    @media only screen and (min-width: 1367px) {
        .bg-image-component {
            .bg-image-wrapper {
                &.bg-xxl-50-50 {
                    .bg-image {
                        background-position: 50% 50%;
                    }
                }
            }
        }
    }
</style>